// @flow
import {isEmpty} from 'lodash';
import React, {PureComponent} from 'react';
import {
  RefreshControl,
  ScrollView,
  StyleSheet,
  View,
  Platform,
} from 'react-native';
import runAfterInteractions from '../../utils/runAfterInteractions';
import {Separator, Text} from 'trpc-react-native-ui';
import {ConfirmationModal, FullButton} from '../../components/common';

import {InternationalHeader} from '../../components/international';
import OrderSummary from '../../components/order/OrderSummary';
import TopUpSliders from '../../components/topUp/TopUpSliders';
import config from '../../config';
import I18n from '../../lib/i18n';
import theme from '../../theme-config';

import type {
  NavigationRoute,
  NavigationScreenProp,
} from '@react-navigation/compat';
import {SelectedVoucherItem} from '../Vouchers/components/SelectedVoucherItem';
import VouchersFrame from '../Vouchers/components/VouchersFrame';

class TopUp extends PureComponent<Props> {
  styles = getStyles();

  componentDidMount() {
    runAfterInteractions(() => {
      this.props.initTopUp();
      this.props.fetchAvailableTopUps();
    });

    if (this.props.setVoucherDetailsComeFrom) {
      this.props.setVoucherDetailsComeFrom(this.props.orderType ?? '');
    }
  }

  componentWillUnmount() {
    this.props.clearTopUp();
    if (this.props.currentVoucherDetails) {
      this.props.setCurrentVoucherDetails(null);
    }
  }

  goToNewPaymentMeanPage = () =>
    this.props.navigation.navigate('registerPaymentDetails', {
      redirectTo: 'newPaymentMean',
    });

  renderConfirmButton = () => {
    const price = this.props.price === undefined ? 0 : this.props.price;

    if (config.displayConfirmationModalOnTopup) {
      const modalTopUpExplanation = this.props.modalTopUpExplanation(
        this.props.walletBalance,
        this.props.nextRenewalDate,
        this.props.daysLeft,
        this.props.currentVoucherDetails,
      );

      return (
        <ConfirmationModal
          testID="topup-open-modal-button"
          disabled={price === 0}
          onConfirm={this.props.topUp}
          openModalText={this.props.confirmButtonText}
          confirmContent={modalTopUpExplanation}
          title={I18n.t('confirmOrder')}
          thirdButtonAction={this.goToNewPaymentMeanPage}
          thirdButtonText={I18n.t('SignUp.ConfirmPlan.modal.thirdButtonText')}
          isLoading={this.props.isConfirmationLoading}
        />
      );
    }
    return (
      <FullButton
        testID="topup-confirm-button"
        style={this.styles.fullbutton}
        onPress={this.props.topUp}
        text={this.props.confirmButtonText}
        disabled={price === 0}
      />
    );
  };

  renderInterNationalHeaderHeader = () => {
    return (
      <InternationalHeader
        iconName={this.props.iconName}
        iconColor={this.props.iconColor}
        image={this.props.headerImage}
        imageStyle={{
          width: 86,
          height: 102,
          resizeMode: 'contain',
          marginHorizontal: 15,
        }}
        title={this.props.headerTitle || ''}
        linkText={I18n.t('ChooseInternationalOptions.linkText')}
        textLine1={this.props.headerTextLine1 || ''}
        countryList={this.props.countryList}
      />
    );
  };

  defineParentScreen = () => {
    if (this.props.availableTopUps) {
      // eslint-disable-next-line dot-notation
      const dataTopUps = this.props.availableTopUps['DATA'];
      if (dataTopUps) {
        // parent screen is "Booster"
        return 'booster';
      }
    }
    if (this.props.travelZone) {
      // parent screen is "Pass voyage"
      return 'travelTopUp';
    } else {
      // parent screen is "Appeler vers l'étranger"
      return 'chooseInternationalOptions';
    }
  };

  renderVouchersFrame = () => {
    const isVoucherDisplayableAndNotEmpty =
      this.props.displayVouchersForTopup &&
      !this.props.currentVoucherDetails &&
      this.props.filteredAvailableVouchers &&
      this.props.filteredAvailableVouchers.length > 0;

    if (isVoucherDisplayableAndNotEmpty) {
      return (
        <VouchersFrame
          comeFrom="topup"
          parentScreen={this.defineParentScreen()}
        />
      );
    }

    return null;
  };

  onPressSelectedVoucher = () => {
    this.props.navigation.navigate('voucherDetails', {
      voucherDetails: this.props.currentVoucherDetails,
      disabledButton: true,
    });
  };

  render() {
    const styles = this.styles;
    const footerTopUpExplanation: ?string = this.props.footerTopUpExplanation(
      this.props.walletBalance,
      this.props.nextRenewalDate,
      this.props.daysLeft,
    );

    const price = this.props.price === undefined ? 0 : this.props.price;
    const selectedTopUps = Object.values(this.props.selectedTopUps);

    const optionRowInfos = {
      availableTopUps: this.props.availableTopUps,
      getSelectedVoiceTopUp: this.props?.getSelectedVoiceTopUp,
      selectedTopUps,
      travelZone: this.props.travelZone,
    };

    if (this.props.route.params?.hideBodyPage) {
      return this.renderInterNationalHeaderHeader();
    }

    return (
      <View style={styles.container}>
        <ScrollView
          refreshControl={
            <RefreshControl
              onRefresh={this.props.fetchAvailableTopUps}
              refreshing={this.props.isLoading}
            />
          }
          contentContainerStyle={styles.scrollView}>
          {(this.props.iddZone || this.props.travelZone) &&
            this.renderInterNationalHeaderHeader()}
          {this.renderVouchersFrame()}
          <TopUpSliders
            addTopUpToSelection={this.props.addTopUpToSelection}
            availableTopUps={this.props.availableTopUps}
            isLoading={this.props.isLoading}
            optionRowInfos={optionRowInfos}
            priceKey={'taxIncludedAmount'}
            selectedTopUps={selectedTopUps}
            withSecondaryRow={!!this.props.travelZone}
            withZoneValidity={this.props.withZoneValidity}
          />
        </ScrollView>
        <Separator />
        {this.props.displayVouchersForTopup &&
        this.props.currentVoucherDetails ? (
          <SelectedVoucherItem
            selectedVoucher={this.props.currentVoucherDetails}
            onPressSelectedVoucher={this.onPressSelectedVoucher}
          />
        ) : null}
        {!this.props.isLoading && isEmpty(this.props.availableTopUps) ? (
          <Text style={styles.pullToRefresh}>
            {I18n.t('Dashboard.TopUp.pullToRefresh')}
          </Text>
        ) : (
          <OrderSummary
            label={this.props.orderSummaryTitle}
            totalPrice={price}
            message={footerTopUpExplanation}
          />
        )}

        {this.renderConfirmButton()}
      </View>
    );
  }
}

type Props = {
  navigation: NavigationScreenProp<NavigationRoute>,
  route: NavigationScreenProp<NavigationRoute>,
} & StateProps &
  DispatchProps &
  TopUpFlaggedProps;

export type TopUpFlaggedProps = {
  confirmButtonText: string,
  footerTopUpExplanation: Function,
  getSelectedVoiceTopUp?: Function,
  headerTextLine1?: string,
  headerTitle?: string,
  headerImage?: Object,
  iconColor?: string,
  iconName?: string,
  modalTopUpExplanation: Function,
  orderSummaryTitle: string,
  withZoneValidity?: boolean,
};

type StateProps = {
  availableTopUps: {[string]: Object[]},
  countryList: [],
  daysLeft?: number,
  iddZone: ?string,
  isConfirmationLoading: boolean,
  isLoading: boolean,
  nextRenewalDate?: number,
  price: number,
  selectedTopUps: Object,
  travelZone: ?string,
  walletBalance?: number,
  currentVoucherDetails: Object,
};

type DispatchProps = {
  addTopUpToSelection: Function,
  clearTopUp: Function,
  fetchAvailableTopUps: () => void,
  initTopUp: Function,
  topUp: Function,
  setCurrentVoucherDetails: Function,
};

function getStyles() {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: 'white',
      justifyContent: 'flex-start',
      paddingBottom: Platform.OS === 'ios' ? 30 : 0,
    },
    fullbutton: {
      margin: 20,
    },
    scrollView: {
      flex: 1,
    },
    pullToRefresh: {
      textAlign: 'center',
      fontSize: 18,
      color: theme.internationalPullToRefreshColor,
      marginTop: 10,
    },
  });
}

export default TopUp;
